import { usePage } from "@inertiajs/react";
import { useState } from "react";
import { DirectionProvider } from "@radix-ui/react-direction";
import { useHotkeys } from "react-hotkeys-hook";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { useTheme } from "@/hooks/useTheme";
import FlashMessages from "@/components/FlashMessages";
import { Toaster } from "@/components/ui/sonner";
import ShortcutsModal from "@/components/ShortcutsModal";
import AwesomeBar from "@/components/AwesomeBar";
import { DeskSubnavProvider } from "@/contexts/DeskSubnavProvider";
import DeskSubnav from "@/components/DeskSubnav";
import AppSidebar from "@/layouts/desk/AppSidebar";
import AppHeader from "@/layouts/desk/AppHeader";
import type { LayoutProps, PageProps } from "@/layouts/desk/types";

export default function DeskLayout({ children, hideSidebar = false }: LayoutProps) {
  const { component, props } = usePage<PageProps>();
  const [direction] = useState<"ltr" | "rtl">(() => {
    // Detect direction from HTML element on mount
    const dir = document.documentElement.getAttribute("dir") as "ltr" | "rtl";
    return dir || "ltr";
  });
  const { toggleTheme } = useTheme();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [awesomeOpen, setAwesomeOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([]);

  const isResourceIndex = component === "Desk/Resources/Index";
  const isResourceShow = component === "Desk/Resources/Show";
  const resource = isResourceIndex || isResourceShow ? props.resource : undefined;
  const query = isResourceIndex ? props.query : undefined;
  const currentModule =
    props.modules?.find((mod) => mod.key === props.current_module) || props.modules?.[0];
  const record = isResourceShow ? (props.record as Record<string, unknown> | undefined) : undefined;

  const recordLabel = (() => {
    if (!record) return "";
    if (resource?.breadcrumb_field && record[resource.breadcrumb_field]) {
      return String(record[resource.breadcrumb_field]);
    }
    if (record.name) return String(record.name);
    if (record.email) return String(record.email);
    if (record.id) return `#${record.id}`;
    return "";
  })();

  useHotkeys(
    "mod+s",
    (event) => {
      if (!isResourceShow) return;
      event.preventDefault();
      const form = document.getElementById("resource-form") as HTMLFormElement | null;
      form?.requestSubmit();
    },
    {
      enableOnFormTags: true,
      enableOnContentEditable: true,
      preventDefault: true,
    }
  );

  useHotkeys(
    "mod+m",
    (event) => {
      event.preventDefault();
      window.location.href = "/desk/modules";
    },
    {
      enableOnFormTags: true,
      enableOnContentEditable: true,
      preventDefault: true,
    }
  );

  const header = (
    <AppHeader
      onOpenShortcuts={() => setShortcutsOpen(true)}
      onToggleTheme={toggleTheme}
      showSidebarToggle={!hideSidebar}
    />
  );

  const content = (
    <DeskSubnavProvider value={{ selectedIds, setSelectedIds }}>
      {(isResourceIndex || isResourceShow) && resource && (
        <DeskSubnav
          mode={isResourceShow ? "show" : "index"}
          label={resource.label}
          module={
            currentModule
              ? {
                  label: currentModule.label,
                  icon: currentModule.icon,
                  color: currentModule.color,
                  path: currentModule.path,
                }
              : undefined
          }
          recordLabel={recordLabel}
          paths={resource.paths}
          views={resource.views}
          currentView={resource.view}
          query={query}
          selectedIds={selectedIds}
          onClearSelection={() => setSelectedIds([])}
        />
      )}

      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        <Toaster />
        <FlashMessages />
        <ShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
        <AwesomeBar open={awesomeOpen} onOpenChange={setAwesomeOpen} />
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </DeskSubnavProvider>
  );

  if (hideSidebar) {
    return (
      <DirectionProvider dir={direction}>
        <div className="flex min-h-svh w-full flex-col">
          {header}
          {content}
        </div>
      </DirectionProvider>
    );
  }

  return (
    <DirectionProvider dir={direction}>
      <SidebarProvider>
        <AppSidebar onOpenAwesome={() => setAwesomeOpen(true)} />
        <SidebarInset className="h-full min-h-0">
          {header}
          {content}
        </SidebarInset>
      </SidebarProvider>
    </DirectionProvider>
  );
}
